import { Button } from "@120wateraudit/waterworks";
import dayjs from "dayjs";
import debounce from "lodash/debounce";
import React, { useCallback, useId, useMemo, useState } from "react";
import { useField } from "react-final-form";
import {
  Dropdown as SUIRDropdown,
  Message,
  Search as SUIRSearch,
  SearchResultProps,
} from "semantic-ui-react";
import styled from "styled-components";

import { useGetAllSubmissionPeriodsQuery } from "src/services";
import { SubmissionPeriodType } from "src/types/enums/SubmissionPeriodType";
import System from "src/types/System";
import { formatDate, formatSubmissionPeriodType } from "src/utils/format";

interface Props {
  systemsToAssign: System[];
}

export const ExistingSubmissionPeriod: React.FC<Props> = ({
  systemsToAssign,
}) => {
  const searchInputId = useId().replace(/:/g, '');
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedType, setSelectedType] = useState<SubmissionPeriodType | "all">("all");
  const debouncedSearch = debounce((_, data) => setSearchTerm(data.value), 300);
  const { data: queryData, isFetching } = useGetAllSubmissionPeriodsQuery({
    filters: { active: true, locked: false },
    page: 1,
    pageSize: 100, // Increased to get all periods for filtering
    searchTerm,
  });
  const allSubmissionPeriods = queryData?.data ?? [];

  // Filter submission periods by selected type
  const submissionPeriods = useMemo(() => {
    if (selectedType === "all") {
      return allSubmissionPeriods;
    }
    return allSubmissionPeriods.filter(
      (period) => period.submissionType === selectedType
    );
  }, [allSubmissionPeriods, selectedType]);

  const { input } = useField("submissionPeriod");

  // Get unique types from available periods
  const availableTypes = useMemo(() => {
    const types = new Set<SubmissionPeriodType>();
    allSubmissionPeriods.forEach((period) => {
      types.add(period.submissionType);
    });
    return Array.from(types);
  }, [allSubmissionPeriods]);

  const typeOptions = [
    { key: "all", value: "all", text: "All Types" },
    ...availableTypes.map((type) => ({
      key: type,
      value: type,
      text: formatSubmissionPeriodType(type),
    })),
  ];

  return (
    <div>
      <TypeDropdownContainer>
        <TypeDropdown
          placeholder="Select Type"
          selection
          search
          searchInput={{ id: `existing-submission-period-type-dropdown-${searchInputId}`, 'aria-label': 'Select submission period type' }}
          options={typeOptions}
          value={selectedType}
          onChange={(_, data) => setSelectedType(data.value as SubmissionPeriodType | "all")}
        />
      </TypeDropdownContainer>
      <Search
        fluid
        loading={isFetching}
        onResultSelect={(_, data) =>
          input.onChange({
            ...data.result,
            systems: [
              ...data.result.systems,
              ...systemsToAssign.map((s) => ({ id: s.id })),
            ],
          })
        }
        onSearchChange={debouncedSearch}
        resultRenderer={SubmissionPeriodInfo}
        results={submissionPeriods}
      />
      {input.value && (
        <SelectedResult
          dueDate={input.value.dueDate}
          name={input.value.name}
          submissionType={input.value.submissionType}
        />
      )}
      <p style={{ margin: "12px" }}>or</p>
      <CreateNewSubmissionPeriodButton systemsToAssign={systemsToAssign} />
    </div>
  );
};

const TypeDropdownContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
`;

const TypeDropdown = styled(SUIRDropdown)`
  width: 80%;
  margin: auto;
` as unknown as typeof SUIRDropdown;

const Search = styled(SUIRSearch)`
  & .input {
    width: 80%;
    margin: auto;
  }
` as unknown as typeof SUIRSearch;

const SubmissionPeriodInfoContainer = styled.div`
  & > p {
    margin-top: 0 !important;
    margin-bottom: 0.2em !important;
  }
`;

const SubmissionPeriodInfo = ({
  dueDate,
  name,
  submissionType,
  style,
}: SearchResultProps & { submissionType?: SubmissionPeriodType }): JSX.Element => (
  <SubmissionPeriodInfoContainer style={style}>
    <p>
      <strong>Name:</strong> {name}
    </p>
    <p>
      <strong>Type:</strong> {submissionType ? formatSubmissionPeriodType(submissionType) : "--"}
    </p>
    <p>
      <strong>Due Date:</strong> {formatDate(dueDate)}
    </p>
  </SubmissionPeriodInfoContainer>
);

const SelectedResult: React.FC<{
  dueDate?: string;
  name: string;
  submissionType?: SubmissionPeriodType;
}> = (props) => {
  return (
    <Message>
      <Message.Header>Selected Submission Period</Message.Header>
      <SubmissionPeriodInfo
        style={{ marginTop: "8px" }}
        title={props.name}
        {...props}
      />
    </Message>
  );
};

const CreateNewSubmissionPeriodButton: React.FC<Props> = ({
  systemsToAssign,
}) => {
  const {
    input: { onChange },
  } = useField("isNew");
  const submissionPeriodField = useField("submissionPeriod");
  const onClick = useCallback(() => {
    const dueDate = dayjs().add(4, "month");
    const newSubmissionPeriod = {
      dueDate: dueDate.format("YYYY-MM-DD"),
      name: dueDate.format("[Q]Q - YYYY"),
      systems: [...systemsToAssign.map((s) => ({ id: s.id }))],
    };
    submissionPeriodField.input.onChange(newSubmissionPeriod);
    onChange(true);
  }, [onChange, submissionPeriodField.input.onChange]);

  return (
    <Button onClick={onClick} variant="secondary">
      + Create Submission Period
    </Button>
  );
};
